/* eslint-disable react/display-name */
import React, {
  useImperativeHandle,
  forwardRef,
  useState,
  useEffect,
} from 'react';
import { useSelector } from 'react-redux';
import { selectDynamicLayout } from '../../redux/slices/room';
import {
  useUpdateDynamicLayoutMutation,
  useUploadImageToS3Mutation,
} from '../../redux/streamGoApi';
import { DynamicLayoutSection } from './DynamicLayoutSection';
import { WebinarType } from './enums';
import {
  useBackgroundSettings,
  useBorderSettings,
  useLogoSettings,
  useLowerThirdsSettings,
  useNameTagSettings,
} from './hooks';
import { useStreamingUrlsSettings } from './hooks/useRoomStreaming';
import { Container } from './styled';
import { IDynamicLayout } from './types';

interface IDynamicLayoutProps {
  roomId: string;
  setIsSaving: (loading: boolean) => void;
  showRoomStreamings?: boolean;
  webinarType?: WebinarType;
}

interface IDynamicLayoutRef {
  saveDynamicLayout: () => void;
}

export const DynamicLayout = forwardRef<IDynamicLayoutRef, IDynamicLayoutProps>(
  ({ roomId, setIsSaving, showRoomStreamings = false, webinarType }, ref) => {
    const [dynamicLayout, setDynamicLayout] = useState<IDynamicLayout>();
    const dl = useSelector(selectDynamicLayout);
    const isRtmp = webinarType === WebinarType.RtmpEncoder;

    useEffect(() => {
      if (JSON.stringify(dl) !== JSON.stringify(dynamicLayout)) {
        setDynamicLayout(dl);
      }
    }, [dl, dynamicLayout]);

    const [uploadImageToS3] = useUploadImageToS3Mutation();
    const [updateDynamicLayout, { isLoading }] =
      useUpdateDynamicLayoutMutation();

    useEffect(() => {
      setIsSaving(isLoading);
    }, [isLoading, setIsSaving]);

    const { getNameTagModel, nameTagBlocks } = useNameTagSettings(
      dynamicLayout?.nameTags,
    );
    const { borderBlocks, getBorderModel } = useBorderSettings(
      dynamicLayout?.videoBorder,
    );
    const { backgroundBlocks, getBackgroundModel } = useBackgroundSettings({
      initialBackground: dynamicLayout?.background,
      roomId,
      uploadImageToS3,
    });
    const { getLowerThirdsModel, lowerThirdsBlocks } = useLowerThirdsSettings({
      lowerThirds: dynamicLayout?.lowerThirds,
    });
    const { getLogoModel, logoBlocks } = useLogoSettings({
      initialLogo: dynamicLayout?.logo,
      roomId,
      uploadImageToS3,
    });
    const { getStreamingUrlsModel, streamingUrlsBlocks } =
      useStreamingUrlsSettings();

    useImperativeHandle(ref, () => ({
      saveDynamicLayout() {
        const layout: IDynamicLayout = {
          nameTags: getNameTagModel(),
          videoBorder: getBorderModel(),
          background: getBackgroundModel(),
          logo: getLogoModel(),
          lowerThirds: getLowerThirdsModel(),
        };
        updateDynamicLayout({
          roomId,
          dynamicLayout: JSON.stringify(layout),
          roomStreamings: showRoomStreamings
            ? getStreamingUrlsModel()
            : undefined,
        });
      },
    }));

    if (dynamicLayout === undefined) {
      return <></>;
    }

    return (
      <Container id="dynamicLayoutSection">
        {showRoomStreamings ? (
          <DynamicLayoutSection
            blocks={streamingUrlsBlocks}
            title="Additional RTMP Streams"
          />
        ) : null}
        <DynamicLayoutSection blocks={lowerThirdsBlocks} title="Lower third" />
        <DynamicLayoutSection blocks={backgroundBlocks} title="Background" />
        {isRtmp ? null : (
          <>
            <DynamicLayoutSection blocks={nameTagBlocks} title="Name Tag" />
            <DynamicLayoutSection blocks={borderBlocks} title="Border" />
          </>
        )}
        <DynamicLayoutSection
          blocks={logoBlocks}
          divider={false}
          title="Logo"
        />
      </Container>
    );
  },
);
